import type {
  BranchSelectionPayload,
  CarePlanDetail,
  CarePlanStepStatusPayload,
  CreateCarePlanPayload,
  CycleModificationMode,
  SaveCarePlanCyclePayload,
  SaveCarePlanStepPayload,
  UpdateCarePlanPayload,
} from "@/types/care-plan";

import { requestData } from "@/api/api-config";

const basePath = "user/patient";

async function listCarePlans(
  patientId: string,
): Promise<CarePlanDetail[] | null> {
  return await requestData<CarePlanDetail[]>(
    `${basePath}/${patientId}/care-plans`,
    "get",
    undefined,
    false,
  );
}

async function getCarePlan(id: string): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${id}`,
    "get",
    undefined,
    false,
  );
}

async function createCarePlan(
  data: CreateCarePlanPayload,
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans`,
    "post",
    { json: data },
    false,
  );
}

async function updateCarePlan(
  id: string,
  data: UpdateCarePlanPayload,
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${id}`,
    "put",
    { json: data },
    false,
  );
}

async function saveCarePlanSteps(
  carePlanId: string,
  steps: SaveCarePlanStepPayload[],
  options?: {
    cycles?: SaveCarePlanCyclePayload[];
    branchSelections?: BranchSelectionPayload[];
    cycleModificationMode?: CycleModificationMode;
  },
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${carePlanId}/steps`,
    "put",
    {
      json: {
        steps,
        ...(options?.cycles != null && { cycles: options.cycles }),
        ...(options?.branchSelections != null && {
          branch_selections: options.branchSelections,
        }),
        ...(options?.cycleModificationMode != null && {
          cycle_modification_mode: options.cycleModificationMode,
        }),
      },
    },
    false,
  );
}

async function updateStepStatus(
  carePlanId: string,
  stepId: string,
  data: CarePlanStepStatusPayload,
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${carePlanId}/steps/${stepId}/status`,
    "patch",
    { json: data },
    false,
  );
}

async function scheduleStep(
  carePlanId: string,
  stepId: string,
  eventId: string,
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${carePlanId}/steps/${stepId}/schedule`,
    "post",
    { json: { event_id: eventId } },
    false,
  );
}

async function selectBranch(
  carePlanId: string,
  stepId: string,
  branchId: string,
): Promise<CarePlanDetail | null> {
  return await requestData<CarePlanDetail>(
    `${basePath}/care-plans/${carePlanId}/steps/${stepId}/select-branch`,
    "post",
    { json: { branch_id: branchId } },
    false,
  );
}

async function deleteCarePlan(id: string): Promise<boolean | null> {
  return await requestData<boolean>(
    `${basePath}/care-plans/${id}`,
    "delete",
    undefined,
    false,
  );
}

export interface CarePlanLogEntry {
  id: string;
  carePlanId: string;
  stepId: string | null;
  action: string;
  fieldName: string | null;
  oldValue: string | null;
  oldValueId: string | null;
  newValue: string | null;
  newValueId: string | null;
  source: string;
  batchId: string | null;
  createdById: string | null;
  createdDate: string;
  cycleId: string | null;
  cyclePass: number | null;
}

async function getCarePlanLogs(
  carePlanId: string,
): Promise<CarePlanLogEntry[] | null> {
  return await requestData<CarePlanLogEntry[]>(
    `${basePath}/care-plans/${carePlanId}/logs`,
    "get",
    undefined,
    false,
  );
}

export const carePlanApi = {
  list: listCarePlans,
  get: getCarePlan,
  create: createCarePlan,
  update: updateCarePlan,
  saveSteps: saveCarePlanSteps,
  updateStepStatus: updateStepStatus,
  scheduleStep: scheduleStep,
  selectBranch,
  delete: deleteCarePlan,
  getLogs: getCarePlanLogs,
};
